import { useMemo } from 'react'
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { InboxIcon, PlusIcon, ShieldAlertIcon, UploadIcon, XIcon } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ems/ui/components/alert'
import { Button } from '@ems/ui/components/button'
import { SearchInput } from '@ems/ui/patterns/search-input'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDate, formatMoney } from '@ems/ui/lib/format'

import { OptionSelect } from '@ems/ui/patterns/option-select'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import { useLiveSearch } from '#admin/lib/use-live-search.ts'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import {
  awaitingAction,
  label,
  PAYMENT_STATUSES,
  paymentTone,
  REGISTRATION_STATUSES,
  registrationTone,
  VERIFICATION_STATUSES,
  verificationTone,
  type RegistrationSummary,
} from '#admin/features/registrations/api.ts'
import {
  pendingVerificationCountQuery,
  registrationsQuery,
} from '#admin/features/registrations/queries.ts'
import { formSettingsQuery } from '#admin/features/registration-form/queries.ts'
import { ticketTypeOptions } from '#admin/features/ticket-types/api.ts'
import { ticketTypesQuery } from '#admin/features/ticket-types/queries.ts'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { BulkActionBar } from '#admin/features/registrations/bulk-action-bar.tsx'
import {
  allSelectedText,
  canBulkSelect,
  pageSelectedText,
} from '#admin/features/registrations/bulk-verification.ts'
import { ExportButton } from '#admin/features/registrations/export-button.tsx'
import { useBulkSelection } from '#admin/features/registrations/use-bulk-selection.ts'
import { RegistrationRowActions } from '#admin/features/registrations/registration-row-actions.tsx'
import {
  SelectionProvider,
  SelectPageBox,
  SelectRowBox,
} from '#admin/features/registrations/selection-boxes.tsx'
import { filtersOf, parseSearch, type Search } from '#admin/features/registrations/search.ts'
import { RegistrationsSkeleton } from '#admin/features/registrations/registration-skeletons.tsx'

export const Route = createFileRoute('/_authenticated/ticketing/registrations/')({
  pendingComponent: RegistrationsSkeleton,
  validateSearch: parseSearch,
  loaderDeps: ({ search }) => filtersOf(search),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(registrationsQuery(deps)),
      context.queryClient.ensureQueryData(pendingVerificationCountQuery()),
      context.queryClient.ensureQueryData(formSettingsQuery()),
      context.queryClient.ensureQueryData(ticketTypesQuery()),
    ]),
  component: RegistrationsPage,
})

/** `all` rather than `''`: Radix reserves the empty string for "nothing chosen". */
const ANY = 'all'
const optionsFor = (values: readonly string[], anyLabel: string) => [
  { value: ANY, label: anyLabel },
  ...values.map((value) => ({ value, label: label(value) })),
]

/**
 * Registrations, with **three status columns** — invariant 06 reaching the screen.
 *
 * A single badge would have to pick a side on "paid but awaiting approval", which is exactly
 * the combination the three axes exist to express. The "next" column is derived from all three
 * together rather than from any one of them.
 *
 * `DataTable` renders the same columns as a stacked card below `md`, which is why the reference is
 * marked `primary`: it becomes the card's heading.
 */
function RegistrationsPage() {
  // Import and New are writes; a view-only role gets the list and the export, which is a read.
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'ticket.manage')
  const search = Route.useSearch()
  const filters = filtersOf(search)
  const navigate = useNavigate({ from: Route.fullPath })
  // The search runs as the operator types, 250 ms after the last key (`useLiveSearch`). The term
  // replaces the address, so Back leaves the search in one step and not one letter at a time.
  const [draft, setDraft] = useLiveSearch(
    filters.q,
    (term) => void navigate({ search: (prev) => parseSearch({ ...prev, q: term }), replace: true }),
  )
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    registrationsQuery(filters),
  )
  const registrations = data.pages.flatMap((page) => page.items)
  const total = data.pages[0]?.total ?? 0
  // One page at a time, 10 rows unless the operator chooses more. Any filter, search or new page
  // size starts again at the first page.
  const listKey = [
    filters.q,
    filters.status,
    filters.paymentStatus,
    filters.verificationStatus,
    filters.ticketTypeId,
    filters.awaitingCompanyInvoice,
  ].join('|')
  const { page, setPage, pageSize, setPageSize } = usePagination(listKey)
  const paged = pageSlice(registrations, page, pageSize)

  // Rows selected for a bulk action, and the bar's "Select all".
  const bulk = useBulkSelection({
    listKey,
    filters,
    loaded: registrations,
    pageRows: paged.rows,
    hasNextPage,
    fetchNextPage,
    enabled: canManage,
  })
  const { selectableKey } = bulk
  const filtered = Object.values(filters).some((value) => value !== '')

  // Through the same parser as the URL, so `all` and an empty search simply drop the key.
  const setFilter = (key: keyof Search, value: string) =>
    void navigate({ search: (prev) => parseSearch({ ...prev, [key]: value }) })

  /**
   * Memoized because TanStack Table compares by reference: a fresh `columns` array every render
   * resets the table's internal state on every render.
   */
  const columns = useMemo<ColumnDef<RegistrationSummary, never>[]>(() => {
    const selectable = selectableKey === '' ? [] : selectableKey.split(',')
    return [
      // Only for a manager, and only while this page has an open row (not canceled or completed).
      ...(canManage && selectable.length > 0
        ? [
            {
              id: 'select',
              meta: { shrink: true },
              // Below `md` each row is a card and this header is its label, so the card says
              // "Select" and the box that selects the whole page is in the wide table only.
              header: () => (
                <>
                  <span className="md:hidden">Select</span>
                  <span className="max-md:hidden">
                    <SelectPageBox
                      ids={selectable}
                      label="Select the open registrations on this page"
                    />
                  </span>
                </>
              ),
              cell: ({ row }: { row: { original: RegistrationSummary } }) =>
                canBulkSelect(row.original) ? (
                  <SelectRowBox
                    id={row.original.id}
                    label={`Select ${row.original.code ?? 'the draft'}`}
                  />
                ) : null,
            } satisfies ColumnDef<RegistrationSummary, never>,
          ]
        : []),
      {
        accessorKey: 'code',
        header: 'Reference',
        meta: { primary: true },
        // A draft has no reference yet, and saying so beats an empty cell.
        cell: ({ row }) => (
          <Link
            to="/ticketing/registrations/$registrationId"
            params={{ registrationId: row.original.id }}
            // Stretched over the row, so the whole row opens the registration (#28).
            className="font-mono text-xs after:absolute after:inset-0 hover:underline"
          >
            {row.original.code ?? 'draft'}
          </Link>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        cell: ({ row }) => (
          <span className="flex flex-col">
            <span>{row.original.contactName}</span>
            <span className="text-muted-foreground text-xs">{row.original.contactEmail}</span>
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={registrationTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Payment',
        cell: ({ row }) => (
          <StatusBadge tone={paymentTone(row.original.paymentStatus)}>
            {label(row.original.paymentStatus)}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'verificationStatus',
        header: 'Verification',
        cell: ({ row }) => (
          <StatusBadge tone={verificationTone(row.original.verificationStatus)}>
            {label(row.original.verificationStatus)}
          </StatusBadge>
        ),
      },
      {
        id: 'next',
        header: 'Next',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {awaitingAction(row.original) ?? 'Nothing outstanding'}
          </span>
        ),
      },
      {
        id: 'total',
        header: 'Total',
        meta: { align: 'end' },
        // Formatting only, never arithmetic: every figure here was computed by the server.
        cell: ({ row }) => formatMoney(row.original.total, row.original.currency),
      },
      {
        // The sort key, so it explains the order the operator is looking at.
        accessorKey: 'createdAt',
        header: 'Registered',
        meta: { hideOnCard: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">{formatDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: 'end', shrink: true },
        // The steps `availableActions` allows on this row. A view-only role gets none.
        cell: ({ row }) =>
          canManage ? <RegistrationRowActions registration={row.original} /> : null,
      },
    ]
  }, [canManage, selectableKey])

  const { data: pendingVerification } = useSuspenseQuery(pendingVerificationCountQuery())
  const { data: ticketTypes } = useSuspenseQuery(ticketTypesQuery())

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Registrations"
        description="Every registration in this edition, whether or not it has been paid."
        actions={
          <>
            <ExportButton search={search} filtered={filtered} canManage={canManage} />
            {canManage ? (
              <>
                <Button asChild variant="outline">
                  <Link to="/ticketing/registrations/import">
                    <UploadIcon />
                    Import
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/ticketing/desk">
                    <PlusIcon />
                    New registration
                  </Link>
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="registrations" />}

      {pendingVerification > 0 && filters.verificationStatus !== 'pending' ? (
        <Alert className="flex items-center gap-3.5 py-3">
          <div
            aria-hidden="true"
            className="bg-warning/15 text-warning flex size-9 shrink-0 items-center justify-center rounded-lg"
          >
            <ShieldAlertIcon className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <AlertTitle className="text-base font-semibold">
              {pendingVerification} pending verification
            </AlertTitle>
            <AlertDescription className="text-sm">
              <span>
                Registrations need your review.{' '}
                <Link
                  to="/ticketing/registrations"
                  search={{ verificationStatus: 'pending' }}
                  className="font-medium underline"
                >
                  Review now →
                </Link>
              </span>
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <div role="search" className="flex w-full min-w-0 sm:w-auto sm:min-w-64 sm:flex-1">
          <SearchInput
            aria-label="Search by reference, name or email"
            placeholder="Search reference, name or email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        <OptionSelect
          aria-label="Status"
          className="w-full sm:w-44"
          value={filters.status === '' ? ANY : filters.status}
          onValueChange={(value) => setFilter('status', value)}
          options={optionsFor(REGISTRATION_STATUSES, 'Any status')}
        />
        <OptionSelect
          aria-label="Payment"
          className="w-full sm:w-44"
          value={filters.paymentStatus === '' ? ANY : filters.paymentStatus}
          onValueChange={(value) => setFilter('paymentStatus', value)}
          options={optionsFor(PAYMENT_STATUSES, 'Any payment')}
        />
        <OptionSelect
          aria-label="Verification"
          className="w-full sm:w-44"
          value={filters.verificationStatus === '' ? ANY : filters.verificationStatus}
          onValueChange={(value) => setFilter('verificationStatus', value)}
          options={optionsFor(VERIFICATION_STATUSES, 'Any verification')}
        />
        <OptionSelect
          aria-label="Ticket type"
          className="w-full sm:w-52"
          value={filters.ticketTypeId === '' ? ANY : filters.ticketTypeId}
          onValueChange={(value) => setFilter('ticketTypeId', value)}
          options={[
            { value: ANY, label: 'Any ticket type' },
            ...ticketTypeOptions(ticketTypes.items),
          ]}
        />
        <OptionSelect
          aria-label="Company invoice"
          className="w-full sm:w-56"
          value={filters.awaitingCompanyInvoice === '' ? ANY : filters.awaitingCompanyInvoice}
          onValueChange={(value) => setFilter('awaitingCompanyInvoice', value)}
          options={[
            { value: ANY, label: 'Any invoice' },
            { value: 'true', label: 'Company invoice to check' },
          ]}
        />
        {/* Beside the filters it clears, and only while one is on. */}
        {filtered ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft('')
              void navigate({ search: {} })
            }}
            className="text-muted-foreground hover:text-foreground h-9 gap-1.5"
          >
            <XIcon className="size-4" />
            Clear filters
          </Button>
        ) : null}
      </div>

      <SelectionProvider ids={bulk.selected} set={bulk.select}>
        <DataTable
          caption="Registrations"
          title={`${filtered ? 'Matching registrations' : 'All registrations'} (${total})`}
          // The total is the server's count of every matching registration. The rows come 50 at a
          // time, so Next asks for the next 50 when its page goes past the rows that are here.
          // Until they arrive, the table stays on the last page it can show. Only with rows: an
          // empty table shows its empty state, and no line under it.
          footer={
            total === 0 ? undefined : (
              <TablePagination
                page={paged.page}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                totalItems={total}
                onPageChange={(nextPage) => {
                  setPage(nextPage)
                  // Not while a fetch is on its way: a second click would ask for the same rows again.
                  if (
                    (nextPage + 1) * pageSize > registrations.length &&
                    hasNextPage &&
                    !isFetchingNextPage
                  ) {
                    void fetchNextPage()
                  }
                }}
                ariaLabel="Pages of registrations"
              />
            )
          }
          columns={columns}
          data={paged.rows}
          getRowId={(registration) => registration.id}
          empty={
            <EmptyState
              icon={<InboxIcon />}
              title={filtered ? 'Nothing matches' : 'No registrations yet'}
              description={
                filtered
                  ? 'The search looks at references, contacts and participants.'
                  : 'They appear here as soon as somebody registers, whether or not they have paid.'
              }
            />
          }
        />
      </SelectionProvider>

      {canManage && bulk.selectedIds.length > 0 ? (
        <BulkActionBar
          registrations={bulk.selectedRows}
          onClear={bulk.clear}
          notice={
            bulk.notice.kind === 'page' ? (
              <>
                {pageSelectedText(bulk.notice.onPage)}{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  disabled={bulk.selectingAll}
                  onClick={() => void bulk.selectAllOpen()}
                >
                  {bulk.selectingAll
                    ? 'Selecting…'
                    : `Select all ${bulk.notice.pending} open registrations`}
                </Button>
              </>
            ) : bulk.notice.kind === 'all' ? (
              <>
                {allSelectedText(bulk.notice.count)}{' '}
                <Button variant="link" size="sm" className="h-auto p-0" onClick={bulk.clear}>
                  Clear selection
                </Button>
              </>
            ) : undefined
          }
        />
      ) : null}
    </div>
  )
}
